import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from 'app/app.service';
import { DetailLocation, OverviewStateService } from '../../services/state.service';
import { AssetGroupingService, AssetListItem, AssetStateService, ErrorLoggerService, MatchMediaService, NavigationService, AssetService, MeasurementUnitsService, UserTaskService, UserTaskItem, nullAccessDenied } from 'app/services';
import { BaseComponent } from 'app/shared/components';
import { KeyDetailsBadge } from 'app/shared/components/cards';
import { getIconSvgString, MAP_COLORS, MAP_COLORS_ENUM } from 'app/shared/components/map/map.markers';
import { ASSET_ICON_INDEX } from 'app/shared/model';
import { Contact, TelemetryIOValue, TelemetryStateResponse, AssetGeoLock, TelemetryIOValues, AssetSharedLocationTokenResponse, GeoLockProfileResponse, AssetLinkage, AssetResponse, DeviceResponse } from '@key-telematics/fleet-api-client';
import { Observable, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SummaryMovement, SummaryTrip } from './details-overview-summary/details-overview-summary.component';
import { AssetStateValue, CustomFieldValue, LinkedAssetValue, IOValue } from './details-sidebar/details-sidebar.component';
import { OverviewDetailsRouteData } from './details.routing';
import { KuiTabBarTitleID } from 'app/key-ui/tab-bar/tab-bar.component';
import { TopNavSettings } from 'app/key-ui/top-nav';
import { ModalService } from 'app/shared/components/modal';
import { KuiSnackbarService } from 'app/key-ui/snackbar/snackbar.service';
import { IKuiDropdownMenuItem } from 'app/key-ui';
import { LocationSelectModalComponent, LocationSelectValues } from 'app/shared/components/modal/location-select-modal/location-select-modal.component';
import { MediaService } from 'app/services/media/media.service';
import { AssetOutputState } from 'app/services/assets/asset.service';
import * as Bluebird from 'bluebird';
import { SharingModalComponent } from './sharing/share-modal.component';
import { AdminEditorService } from 'app/shared/components/admin/admin.service';
import { AssetDetailsMiniSectionHandler } from 'app/shared/components/admin/entities/asset/details-mini.section';
import { getDeviceConfiguration } from 'app/services/api/api.utils';


@Component({
    selector: 'key-overview-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.scss'],
    providers: [UserTaskService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverviewDetailsComponent extends BaseComponent implements OnInit {

    @HostBinding('class.d-flex') flex = true;
    @HostBinding('class.flex-stretch') flexStretch = true;
    @HostBinding('class.flex-column') flexColumn = true;
    @HostBinding('class.min-details-width') minWidth = true;


    asset: AssetListItem;
    mapAssets: AssetListItem[] = [];
    assetBadges: KeyDetailsBadge[];

    mobileHeader: TopNavSettings;
    tabs: KuiTabBarTitleID[];
    tabLoadedIndex: { [key: string]: boolean; } = {}; // track whether a specific tab has loaded

    mapIcon: SafeHtml;
    isMobile: boolean;
    isCompact: boolean;
    isFloating: boolean;

    overviewSummaryMovement: SummaryMovement;
    overviewSummaryTrip: SummaryTrip;

    errorMessage: string;
    activeTab: string;

    io: IOValue[] = [];
    linkedAssets: LinkedAssetValue[] = [];
    linkedFrom: AssetLinkage;
    fields: CustomFieldValue[] = [];
    contacts: Contact[] = [];
    state: AssetStateValue[] = [];
    telemetry: TelemetryStateResponse;
    geoLock: AssetGeoLock;
    geoLockProfile: GeoLockProfileResponse;
    title: string;
    shares: AssetSharedLocationTokenResponse[] = [];

    hasTripReplay$: Observable<boolean>;
    hasAlerts$: Observable<boolean>;

    taskBucket: { [key: string]: UserTaskItem };
    tasks: UserTaskItem[] = [];
    taskFeedSub: Subscription;

    actionsMenu: IKuiDropdownMenuItem[] = [];

    location: string;
    ready = false;

    allowEditing = false;

    customPanels: {
        id: string;
        title: string;
        url: SafeResourceUrl;
        activated: boolean;
    }[] = [];

    hasCameraIO = false;

    tabIcons: { [key: string]: string } = {
        activity: 'eye',
        trips: 'history',
        alerts: 'exclamation-triangle',
        events: 'info-circle',
        tasks: 'tasks',
        videos: 'photo-video',
    };

    @Output() mobileAction: EventEmitter<any> = new EventEmitter();

    constructor(
        public app: AppService,
        private admin: AdminEditorService,
        private activatedRoute: ActivatedRoute,
        private matchMedia: MatchMediaService,
        private sanitizer: DomSanitizer,
        private overviewState: OverviewStateService,
        private error: ErrorLoggerService,
        private ref: ChangeDetectorRef,
        private assetService: AssetService,
        private assetState: AssetStateService,
        private assetGrouping: AssetGroupingService,
        private navigation: NavigationService,
        private modal: ModalService,
        private measure: MeasurementUnitsService,
        private snackbar: KuiSnackbarService,
        private taskService: UserTaskService,
        private media: MediaService,
        private i18n: TranslateService) {
        super();


        this.onAll<any>([
            this.matchMedia.isMobileOrTablet,
            this.overviewState.detailLocation$,
            this.app.client$,
        ], ([mobile, location]) => {
            this.isMobile = mobile;
            this.isFloating = this.app.features.page.overview.layout === 'basic';
            this.mobileHeader = this.isMobile ? {
                title: this.title,
                navigationAction: {
                    icon: 'back',
                    click: () => this.deselectAsset(),
                },
                actions: null,
            } : null;
            this.location = this.isFloating || this.isMobile ? 'left' : location;
            this.minWidth = ['left', 'right'].includes(location) && !this.isCompact;
            this.getActions(this.asset && this.asset.id).then(actions => {
                this.actionsMenu = actions;
                this.ref.markForCheck();
            });

            this.isCompact = this.location !== 'bottom';
            this.allowEditing = this.app.features.page.overview.show.editing && this.app.apiMethodAllowed('entities', 'updateAsset');

            const visibleTabIds = ['activity'];
            if (this.app.features.page.overview.show.activity && (this.isCompact && !this.isMobile)) { visibleTabIds.push('trips'); }
            if (this.app.features.page.overview.show.alerts) { visibleTabIds.push('alerts'); }
            if (this.app.features.page.overview.show.events) { visibleTabIds.push('events'); }
            if (this.hasCameraIO) { visibleTabIds.push('videos'); }

            if (this.app.features.page.overview.show.tasks) { visibleTabIds.push('tasks'); }

            this.tabs = visibleTabIds.map(x => ({
                id: x,
                title: this.i18n.instant(`STATUS.DETAILS.${x.toUpperCase()}`),
                icon: this.tabIcons[x],

            }));

            this.loadCustomPanels(this.asset?.id || '');
            this.tabs = [...this.tabs, ...this.customPanels.map(panel => ({
                id: panel.id,
                title: panel.title,
            }))];


            this.ready = true;
            this.ref.markForCheck();
        });

    }

    ngOnInit() {
        this.activatedRoute.data // listen for changes to our route parameter data
            .pipe(takeUntil(this.destroyed))
            .subscribe(data => this.load(data.params));

        this.assetState.assets$ // listen for updates on the assets state and check if the selected asset is in the list
            .pipe(takeUntil(this.destroyed))
            .subscribe(assets => {
                if (this.asset && assets) {
                    const asset = assets.find(x => x.id === this.asset.id);
                    if (asset) {
                        this.mapAssets = [asset];
                        this.setAsset(asset);
                    }
                }
            });
    }

    ngOnDeactivate() {
        this.clearFeeds();
    }

    load(data: OverviewDetailsRouteData) {

        if (data) {

            if (!data.asset || (this.asset && this.asset.id !== data.asset.id)) {
                this.clearFeeds();
            }

            if (data.err) {
                this.showError(data.err);
            } else {
                this.setAsset(data.asset);
                this.setActiveTab(data.tab || 'activity');
                this.showError(null);
            }
            this.ref.markForCheck();
        }
    }

    clearFeeds() {
        if (this.taskFeedSub) {
            this.taskFeedSub.unsubscribe();
            this.taskFeedSub = null;
        }
        this.tabLoadedIndex = {}; // will free the feeds and allow them to do necessary cleanup
        // this.tasks = [];
        this.ref.markForCheck();
    }

    async setAsset(asset: AssetListItem) {
        if (asset) {

            this.title = asset.name;
            this.mapIcon = this.getMapIcon(asset);

            this.io = [];
            this.fields = [];
            this.state = [];
            this.linkedAssets = [];
            this.telemetry = null;
            this.geoLock = asset.geoLock;

            // overview summary
            this.overviewSummaryMovement = null;
            this.overviewSummaryTrip = null;

            if (asset.telemetry) {
                this.telemetry = asset.telemetry;

                const speedLimit = asset.telemetry.spd?.md || asset.telemetry.spd?.rd;

                this.overviewSummaryMovement = {
                    date: asset.telemetry.date,
                    assetType: asset.assetType.name,
                    active: asset.telemetry.active,
                    lastMovement: asset.telemetry.lastMovement,
                    lastTripEnd: asset.telemetry.lastTripEnd,
                    location: asset.telemetry.location && asset.telemetry.location.address,
                    speed: asset.telemetry.location && asset.telemetry.location.speed,
                    speedLimit: speedLimit && this.measure.fromBackend('speed', asset.telemetry.spd?.un === 0 ? speedLimit : speedLimit * 1.609344, 0),
                };

                if (asset.telemetry.linked) {
                    Promise.all(asset.telemetry.linked.filter(x => x.id !== asset.id).map(x => this.app.api.entities.getAsset(x.id, this.app.api.cacheFor(5)))).then(assets => {
                        this.linkedAssets = assets.map(x => ({
                            name: x.name,
                            id: x.id,
                            type: x.assetType.name,
                            icon: this.getIcon(x.assetType.name, x.color),
                        }));
                        this.ref.markForCheck();
                    }).catch(err => {
                        // ignore errors here. at worst linked assets won't be shown this time and will be retried when the device updates again
                        console.error(err);
                    });
                }

                if (asset.telemetry.trip && asset.telemetry.active) {
                    this.overviewSummaryTrip = {
                        addressStart: asset.telemetry.trip.startAddress,
                        dateStart: asset.telemetry.trip.start,
                        distance: asset.telemetry.trip.distance,
                    };
                }

                if (asset.telemetry.io) {
                    this.parseIOs(asset.id, asset.telemetry.io).then(result => {
                        if (this.asset?.id === asset.id) {
                            this.io = result;
                            this.ref.markForCheck();
                        }
                    });
                }

                if (asset.telemetry.state) {
                    this.overviewState.getAssetStateProfiles(asset.owner.id).then(profiles => {
                        if (profiles) {
                            this.state = Object.keys(asset.telemetry.state).map(key => {
                                const profile = profiles.items.find(x => x.id === key);
                                if (profile) {
                                    const state = asset.telemetry.state[key];
                                    const item = profile.items.find(x => x.state === state.state);
                                    if (item) {
                                        return {
                                            id: key,
                                            name: profile.name,
                                            color: state.color,
                                            value: item.state,
                                        };
                                    }
                                }
                                return null;
                            }).filter(x => x);
                            this.ref.markForCheck();
                        }
                    }).catch(err => {
                        // ignore errors here. at worst state won't be shown this time and will be retried when the device updates again
                        console.error(err);
                    });
                }
            }

            if (asset.fields && this.app.client.customFields) {
                const customFields = [
                    ...(this.app.client.customFields.asset || []),
                    ...(this.app.client.customFields[asset.assetType.id] || []),
                ];
                this.fields = Object.keys(asset.fields).map(key => {
                    const cf = customFields.find(x => x.id === key);
                    if (cf) {
                        return {
                            id: key,
                            name: cf.title,
                            value: asset.fields[key],
                            type: cf.type
                        };
                    }
                    return null;
                }).filter(x => x);
            }

            if (!this.asset || this.asset.id !== asset.id) {
                this.contacts = [];
                this.assetBadges = [];
                this.actionsMenu = [];
                this.linkedFrom = null;
                Promise.all([
                    this.app.api.entities.getAsset(asset.id, this.app.api.cacheFor(5)),
                    this.assetGrouping.getAssetGroups(),
                ]).then(([item, groups]) => {
                    this.contacts = item.contacts || [];
                    // add all of the contacts from the asset groups that the asset belongs to
                    groups.filter(x => !!item.groups.find(g => g.id === x.id)).forEach(group => {
                        (group.contacts || []).forEach(contact => {
                            if (!this.contacts.find(x => x.id === contact.id)) {
                                this.contacts.push(contact);
                            }
                        });
                    });
                    if (item.owner.id !== this.app.client.id && !this.assetBadges.find(x => x.text === item.owner.name)) {
                        this.assetBadges.push({
                            text: item.owner.name,
                            icon: 'home',
                            hint: this.i18n.instant('STATUS.DETAILS.SHARED_HINT', { client: item.owner.name }),
                        });
                    }

                    if (item.geoLockProfile) {
                        this.app.api.entities.getGeoLockProfile(item.geoLockProfile.id, this.app.api.cacheFor(5)).then(profile => {
                            this.geoLockProfile = profile;
                            this.ref.markForCheck();
                        }, error => console.error(error));
                    }
                    this.linkedFrom = (item.linkedTo && item.linkedTo[0]) || item.linkedFrom;
                    this.ref.markForCheck();
                });

                this.tasks = [];
                this.taskBucket = {};
                this.taskFeedSub = this.taskService.getTaskFeed(asset.id).subscribe(results => {
                    results.filter(x => x.task.type !== 'DeviceVideoTask').forEach(item => {
                        this.taskBucket[item.id] = item;
                    });
                    this.tasks = Object.keys(this.taskBucket).reduce((arr, key) => [...arr, this.taskBucket[key]], []);
                    this.ref.markForCheck();
                    // redo the io's after a task change is received, we need to re-check their state
                    if (this.asset && this.asset.telemetry && this.asset.telemetry.io) {
                        const assetId = this.asset.id;
                        this.parseIOs(assetId, this.asset.telemetry.io).then(result => {
                            if (this.asset.id === assetId) {
                                this.io = result;
                                this.ref.markForCheck();
                            }
                        });
                    }
                }, error => {
                    // we should only get errors if the taskmanager is down or busy restarting, so just log it to the console.
                    console.error('Unable to read task feed', error);
                });

                this.shares = [];
                this.loadSharedLocationTokens(asset.id);

                this.loadCustomPanels(asset.id);

                this.getActions(asset.id).then(actions => {
                    this.actionsMenu = actions;
                    this.ref.markForCheck();
                });

            }

            this.tabs = this.tabs?.filter(x => x.id !== 'videos');

            this.hasCameraIO = !!(
                asset?.telemetry?.io && Object.keys(asset.telemetry.io).length > 0 &&
                Object.keys(asset.telemetry.io).find(x => asset.telemetry.io[x].type === 'camera') &&
                this.app.features.page.overview.show.videos
            );

            if (this.hasCameraIO) {
                this.tabs?.push({
                    id: 'videos',
                    title: this.i18n.instant(`STATUS.DETAILS.VIDEOS`),
                    icon: this.tabIcons['videos'],
                });
            }
        }
        this.asset = asset;
        this.ref.markForCheck();
    }


    loadSharedLocationTokens(assetId: string) {
        if (this.app.features.page.overview.show.sharing) {
            this.app.api.entities.listSharedAssetLocationTokens(undefined, assetId).then(result => {
                this.shares = result.items;
                this.ref.markForCheck();
            }).catch(err => {
                console.error(err);
            });
        }
    }


    loadCustomPanels(assetId: string) {
        if (this.app.features.page.overview.customPanels) {
            this.customPanels = this.app.features.page.overview.customPanels.split('\n').map(str => {
                const [title, ...rest] = str.split(':');
                let url = rest.join(':');
                // substitute some values internal values if they are present in the url
                url = url
                    .replace('{token}', this.app.api.accessToken)
                    .replace('{language}', this.app.user.language || 'en-us')
                    .replace('{user}', this.app.user.id)
                    .replace('{client}', this.app.client.id)
                    .replace('{asset}', assetId);
                return {
                    id: title.toLowerCase().replace(/[^A-Z0-9]+/ig, '_'),
                    title: title,
                    url: this.sanitizer.bypassSecurityTrustResourceUrl(url),
                    activated: false,
                };
            }).filter(x => x);
        }
    }


    getIcon(type?: string, color?: string): { name: string, color: string } {
        const name = (type || this.asset.assetType && this.asset.assetType.name) || 'default';
        return {
            name: ASSET_ICON_INDEX[name.toLowerCase()],
            color: color || this.asset.color,
        };
    }

    setActiveTab(active: string) {
        const prev = this.activeTab;

        if (!this.hasCameraIO && active === 'videos') {
            active = 'activity';
        }
        this.activeTab = active;
        this.tabLoadedIndex[active] = true;
        if (this.asset && this.activeTab !== prev) {
            this.overviewState.navigateToAsset(this.asset.id, active);
        }
    }

    getMapIcon(asset): SafeHtml {
        let iconType = 'idle';
        if (!!asset.telemetry && asset.telemetry.active) {
            if (!!asset.telemetry.location && asset.telemetry.location.speed === 0) {
                iconType = 'stop';
            } else {
                iconType = 'direction';
            }
        }
        const icon = getIconSvgString(this.app.theme, iconType, {
            fill: (typeof asset['color'] === 'number')
                ? MAP_COLORS[asset['color'] || 0]
                : MAP_COLORS[MAP_COLORS_ENUM[asset['color']] || 0],
            rotation: asset.telemetry && asset.telemetry.location.heading || 0,
            size: 50,
        });

        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }


    async parseIOs(assetId: string, bucket: TelemetryIOValues): Promise<IOValue[]> {

        const ioInfo: AssetOutputState[] = (await this.assetService.getOutputStates(assetId).catch(nullAccessDenied)) || [];

        const getIOValue = (io: TelemetryIOValue): string => {
            switch (io.type) {
                case 'temperature':
                    const value = this.measure.format(io.value, 'temperature');
                    return value.value + ' ' + (value.unit || '');
                case 'digital':
                case 'output':
                    return io.text;
                case 'value':
                    return Array.isArray(io.text) ? io.text.join('<br/>') : io.text;
                case 'camera':
                    return 'Request';
                default: {
                    return io.value + ' ' + (io.unit || '');
                }
            }
        };

        const getIOActions = async (io: TelemetryIOValue): Promise<IKuiDropdownMenuItem[]> => {
            const info = ioInfo.find(x => x.input === io.input);
            switch (io.type) {
                case 'output':
                    if (info) {

                        const toggleOutput = (state: number) => {
                            this.assetService.createDeviceOutputTask(assetId, io.name, io.input, state).catch(err => {
                                this.modal.error('Unable to create output task', err);
                            });
                        };

                        return [
                            { text: info.states.active, type: 'action', action: () => { toggleOutput(1); }, closeDropdownOnClicked: true },
                            { text: info.states.inactive, type: 'action', action: () => { toggleOutput(0); }, closeDropdownOnClicked: true },
                        ];
                    } else {
                        return null;
                    }

                case 'camera':

                    const items = [];

                    const settings = await this.media.getVideoRequestSettings(assetId);
                    if (settings) {
                        items.push({
                            text: 'Request Video', type: 'action', action: () => {
                                this.showRequestVideoModal(assetId);
                            }, closeDropdownOnClicked: true,
                        });

                        if (this.app.isSystemUser) { // only allow this for system users for now
                            const cam = settings.cameras.find(x => x.key === io.input);
                            if (cam && cam.live) {
                                items.push({
                                    text: 'Live Video Stream', type: 'action', action: () => {
                                        this.showLiveVideoStreamModal(io);
                                    }, closeDropdownOnClicked: true,
                                });
                            }
                        }

                    }
                    if (info?.device?.features?.camera_take_still) {
                        items.push({
                            text: 'Request Photo', type: 'action', action: () => {
                                this.assetService.createDevicePhotoTask(assetId, io.name, io.input).catch(err => {
                                    this.modal.error('Unable to create camera task', err);
                                });
                            }, closeDropdownOnClicked: true,
                        });
                    }
                    return items;



                default: {
                    return null;
                }
            }
        };

        const getExecutingTasks = (io: TelemetryIOValue): UserTaskItem[] => {
            switch (io.type) {
                case 'output':
                    return this.tasks.filter(x => x.active && x.task.type === 'DeviceOutputTask' && x.task.data.output.output === io.input);
                case 'camera':
                    return this.tasks.filter(x => x.active && x.task.type === 'DevicePhotoTask' && x.task.data.camera.input === io.input);
                default:
                    return [];
            }
        };


        return Bluebird.map(Object.keys(bucket), async key => {
            const io = bucket[key];
            return {
                title: io.name,
                value: getIOValue(io),
                actions: await getIOActions(io),
                tasks: getExecutingTasks(io),
            };
        });

    }

    deselectAsset(closeSidebar = false) {
        this.overviewState.navigateToRoot();
        if (closeSidebar) {
            this.overviewState.closeSidebar();
        }
    }

    showError(error: any) {
        this.error.trackException(error);
        this.errorMessage = error ? error.message || error.error || error : null;
    }

    openReplay() {
        this.navigation.navigateToTrip(this.asset.id, null);
    }

    userAction(actionStr: string) {
        const [type, action, id] = actionStr.split('.');
        switch (type) {
            case 'geolock': return this.geoLockAction(action);
            case 'task': return this.taskAction(action, id);
            case 'share': return this.shareAction(action, id);
        }
    }

    async shareAction(_action: string, id: string) {
        // tslint:disable-next-line: tsr-detect-possible-timing-attacks
        const share = this.shares.find(x => x.token === id);
        await SharingModalComponent.open(this.modal, { share, asset: this.asset });
        this.loadSharedLocationTokens(this.asset.id);

    }


    taskAction(action: string, id: string) {
        switch (action) {
            case 'cancel':
                this.taskService.cancelTask(id).catch(error => {
                    this.modal.error('Unable to cancel task', error);
                });
                break;
            default:
                break;
        }
    }

    geoLockAction(action: string) {
        switch (action) {
            case 'zoom':
                if (this.asset.geoLock) {
                    this.overviewState.zoomTo(this.asset.geoLock.lat, this.asset.geoLock.lon, 15);
                }
                break;
            case 'clear':
                this.assetService.removeGeoLock(this.asset.id)
                    .then(() => {
                        this.snackbar.message(this.i18n.instant(`STATUS.DETAILS.GEO_LOCK.ACTION_RESULT`, {
                            action: this.i18n.instant(`STATUS.DETAILS.GEO_LOCK.UNLOCK`),
                            asset: this.asset.name,
                            status: this.i18n.instant('SHARED.SUCCEEDED').toLowerCase(),
                        }), null, 'lock-open');
                    })
                    .catch(err => {
                        this.snackbar.message(this.i18n.instant(`STATUS.DETAILS.GEO_LOCK.ACTION_RESULT`, {
                            action: this.i18n.instant(`STATUS.DETAILS.GEO_LOCK.UNLOCK`),
                            asset: this.asset.name,
                            status: this.i18n.instant('SHARED.FAILED').toLowerCase(),
                        }) + ' - ' + err.message, null, 'lock-open');
                    });
                break;
            default:
                break;
        }
    }

    async geoLockAsset() {

        if (this.asset.telemetry && this.asset.telemetry.location) {
            const translate = (key, options?) => this.i18n.instant(`STATUS.DETAILS.GEO_LOCK.MODAL.${key}`, options);
            const result = await this.modal.form({
                groups: [{
                    name: translate('TITLE'),
                    description: translate('DESCRIPTION'),
                    fields: [
                        { id: 'distance', type: 'number', title: translate('FIELDS.DISTANCE'), required: true, min: 0.01, max: 100, unit: this.measure.unit('distance') },
                    ],
                }],
            }, { distance: 0.2, });
            if (result) {
                this.assetService.addGeoLock(this.asset.id, this.asset.telemetry.location.lat, this.asset.telemetry.location.lon, this.measure.toBackend('distance', result.distance) as number)
                    .then(() => {
                        this.snackbar.message(this.i18n.instant(`STATUS.DETAILS.GEO_LOCK.ACTION_RESULT`, {
                            action: this.i18n.instant(`STATUS.DETAILS.GEO_LOCK.LOCK`),
                            asset: this.asset.name,
                            status: this.i18n.instant('SHARED.SUCCEEDED').toLowerCase(),
                        }), null, 'lock');
                    })
                    .catch(err => {
                        this.snackbar.message(this.i18n.instant(`STATUS.DETAILS.GEO_LOCK.ACTION_RESULT`, {
                            action: this.i18n.instant(`STATUS.DETAILS.GEO_LOCK.LOCK`),
                            asset: this.asset.name,
                            status: this.i18n.instant('SHARED.FAILED').toLowerCase(),
                        }) + ' - ' + err.message, null, 'lock');
                    });

            }

        }
    }


    async getActions(assetId: string): Promise<IKuiDropdownMenuItem[]> {

        const result: IKuiDropdownMenuItem[] = [];

        if (assetId) {
            const asset = await this.app.api.entities.getAsset(assetId, this.app.api.cacheFor(5));
            if (asset.owner.id === this.app.client?.id) { // don't attempt to get details for shared assets, it will fail
                if (asset.devices && asset.devices.length > 0) {
                    const device = await this.app.api.entities.getDevice(asset.devices[0].id, this.app.api.cacheFor(5)).catch<DeviceResponse>(nullAccessDenied);
                    if (device) {
                        const config = await getDeviceConfiguration(this.app.api, device);
                        Object.keys(config.features).forEach(key => {
                            switch (key) {
                                case 'poll':
                                    result.push({ text: this.i18n.instant(`STATUS.DETAILS.ACTIONS.POLL.TITLE`), type: 'action', closeDropdownOnClicked: true, action: () => this.requestLatestPosition() });
                                    break;
                                case 'voice_dialback':
                                    result.push({ text: this.i18n.instant(`STATUS.DETAILS.ACTIONS.VOICE_DIALBACK.TITLE`), type: 'action', closeDropdownOnClicked: true, action: () => this.requestAudioDialBack() });
                                    break;
                                case 'garmin_fmi':
                                    result.push({ text: this.i18n.instant(`STATUS.DETAILS.ACTIONS.SEND_MESSAGE.TITLE`), type: 'action', closeDropdownOnClicked: true, action: () => this.sendTextMessage() });
                                    result.push({ text: this.i18n.instant(`STATUS.DETAILS.ACTIONS.SEND_ADDRESS.TITLE`), type: 'action', closeDropdownOnClicked: true, action: () => this.sendAddressMessage() });
                                    break;
                                case 'send_text_message':
                                    result.push({ text: this.i18n.instant(`STATUS.DETAILS.ACTIONS.SEND_MESSAGE.TITLE`), type: 'action', closeDropdownOnClicked: true, action: () => this.sendTextMessage() });
                                    break;
                                case 'send_address_message':
                                    result.push({ text: this.i18n.instant(`STATUS.DETAILS.ACTIONS.SEND_ADDRESS.TITLE`), type: 'action', closeDropdownOnClicked: true, action: () => this.sendAddressMessage() });
                                    break;
                            }
                        });
                    }
                }
            }
        }

        if (!this.isFloating && !this.isMobile) {

            if (result.length > 0) {
                result.push({ type: 'divider' });
            }

            const locations = ['left', 'right', 'bottom'].filter(x => x !== this.location);
            locations.forEach(loc => {
                result.push({ text: this.i18n.instant(`STATUS.DETAILS.ACTIONS.MOVE_PANEL.${loc.toUpperCase()}`), type: 'action', closeDropdownOnClicked: true, action: () => this.overviewState.setDetailLocation(loc as DetailLocation), });
            });

        }
        return result;
    }


    requestLatestPosition() {
        return this.assetService.createDevicePollTask(this.asset.id).catch(err => {
            this.showTranslatedTaskErrorModal('poll', err);
        });
    }

    async requestAudioDialBack() {
        const translate = (key, options?) => this.i18n.instant(`STATUS.DETAILS.ACTIONS.VOICE_DIALBACK.MODAL.${key}`, options);
        const result = await this.modal.form({
            groups: [{
                name: translate('TITLE'),
                description: translate('DESCRIPTION'),
                fields: [
                    { id: 'number', type: 'text', title: translate('FIELDS.NUMBER'), required: true },
                ],
            }],
        }, { number: '+', });
        if (result) {
            this.assetService.createDeviceDialbackTask(this.asset.id, result.number).catch(err => {
                this.showTranslatedTaskErrorModal('voice_dialback', err);
            });
        }
    }


    async sendTextMessage() {
        const translate = (key, options?) => this.i18n.instant(`STATUS.DETAILS.ACTIONS.SEND_MESSAGE.MODAL.${key}`, options);
        const result = await this.modal.form({
            groups: [{
                name: translate('TITLE'),
                description: translate('DESCRIPTION'),
                fields: [
                    { id: 'message', type: 'text', title: translate('FIELDS.MESSAGE'), required: true },
                ],
            }],
        }, { message: '', });
        if (result) {
            this.assetService.createDeviceTextMessageTask(this.asset.id, result.message).catch(err => {
                this.showTranslatedTaskErrorModal('send_message', err);
            });
        }
    }

    async sendAddressMessage() {
        const location = await LocationSelectModalComponent.open(this.modal, {
            actionsText: { apply: this.i18n.instant('DIALOG.SEND') },
            title: this.i18n.instant('STATUS.DETAILS.ACTIONS.SEND_ADDRESS.TITLE'),
        } as LocationSelectValues);

        if (location) {
            this.assetService.createDeviceAddressTask(this.asset.id, location.lat, location.lon, location.address).catch(err => {
                this.showTranslatedTaskErrorModal('send_address', err);
            });
        }
    }

    showTranslatedTaskErrorModal(type: string, err: Error) {
        this.modal.error(this.i18n.instant('STATUS.DETAILS.ERROR.UNABLE_TO_CREATE_TASK', {
            task: this.i18n.instant(`STATUS.DETAILS.ACTIONS.${type.toUpperCase()}.TASK`),
        }), err);
    }

    async showRequestVideoModal(assetId: string): Promise<void> {

        const result = await this.media.requestVideoModal(assetId);

        if (result) {
            try {
                await this.media.createVideoEvent(assetId, result.date, result.duration, result.offset, result.cameras);
                this.setActiveTab('events');
            } catch (err) {
                const errorMessage = err?.message.includes('Unable to find telemetry') ? this.i18n.instant('VIDEOS.ERRORS.REQUEST_ERROR') : err?.message;
                this.modal.error(errorMessage);
            }

        }

    }

    async showLiveVideoStreamModal(io: TelemetryIOValue): Promise<void> {
        const duration = 60;
        this.media.liveStreamVideoModal(this.asset.id, io.input, duration);
    }


    async editAsset(asset: AssetListItem) {
        const entity = await this.admin.getCached(`getAsset/${asset.id}`, api => api.entities.getAsset(asset.id), 0);
        const editor = new AssetDetailsMiniSectionHandler(this.admin, entity.owner.id);
        const result = await this.admin.showEditorModal<AssetResponse>(editor, entity);
        if (result) {
            this.assetState.updateAsset(result as AssetListItem);
        }
    }

}
